import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { DECISIONS } from "../data/questions";
import { bandColor } from "../lib/bands";
import { Lock, Crown, ArrowUpRight, Trophy, Check } from "lucide-react";

export default function Compare() {
  const { subscription } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState("");
  const [picked, setPicked] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const active = subscription?.active;
  const limit = subscription?.compare_limit ?? 0;

  useEffect(() => {
    if (!active) return;
    api.get("/assessments").then(({ data }) => {
      setAssessments(data);
      if (data.length) setSelectedAssessment(data[data.length - 1].id);
    });
  }, [active]);

  if (!subscription) {
    return (
      <div className="min-h-screen grain">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 py-20 text-center text-stone font-mono text-sm">Loading…</div>
      </div>
    );
  }

  if (!active) {
    return (
      <div className="min-h-screen grain">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 md:px-8 py-20 text-center relative z-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-3xl bg-brand-orange/10 border border-brand-orange/30 mb-6">
            <Lock size={24} className="text-brand-orange" />
          </div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-ink">Multi-decision comparison is premium.</h1>
          <p className="text-stone mt-4">
            Compare how your current profile fits several major decisions at once, and get a ranked best-fit recommendation. Unlock it with a Scorecaster subscription.
          </p>
          <Link to="/premium" data-testid="compare-upsell" className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-full bg-brand-red text-white font-semibold hover:bg-[#e63f3f] transition-colors duration-300">
            <Crown size={18} /> View plans
          </Link>
        </div>
      </div>
    );
  }

  const toggleDecision = (val) => {
    setResult(null);
    setPicked((prev) => {
      if (prev.includes(val)) return prev.filter((v) => v !== val);
      if (limit != null && prev.length >= limit) {
        toast.error(`Your ${subscription.label} plan compares up to ${limit} decisions.`);
        return prev;
      }
      return [...prev, val];
    });
  };

  const runCompare = async () => {
    if (!selectedAssessment) return toast.error("Select an assessment first.");
    if (picked.length < 2) return toast.error("Pick at least 2 decisions to compare.");
    setLoading(true);
    try {
      const { data } = await api.post("/compare", { assessment_id: selectedAssessment, decisions: picked });
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Comparison failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 relative z-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <span className="label-tiny">Premium · Decision comparison</span>
            <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-2 text-ink">Which move fits best?</h1>
          </div>
          <span className="text-xs font-mono text-stone">
            {limit == null ? "Unlimited decisions" : `Up to ${limit} decisions`} · {subscription.label}
          </span>
        </div>

        <div className="mt-8">
          <label className="label-tiny">Score profile</label>
          <select data-testid="compare-assessment-select" value={selectedAssessment} onChange={(e) => { setSelectedAssessment(e.target.value); setResult(null); }}
            className="mt-2 w-full bg-white border border-clay rounded-2xl px-4 py-3 text-sm text-ink outline-none focus:border-[#DDA15E] transition-colors duration-300">
            {assessments.map((a) => (
              <option key={a.id} value={a.id}>Core {a.scores.core} ({a.scores.band}) · {new Date(a.created_at).toLocaleDateString()}</option>
            ))}
          </select>
        </div>

        <div className="mt-8">
          <label className="label-tiny">Decisions to compare {limit != null && `(${picked.length}/${limit})`}</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
            {DECISIONS.filter((d) => d.value !== "other").map((d) => {
              const on = picked.includes(d.value);
              return (
                <button key={d.value} data-testid={`compare-pick-${d.value}`} onClick={() => toggleDecision(d.value)}
                  className={`flex items-center gap-2 py-3 px-3 rounded-2xl border text-sm transition-all duration-300 ${on ? "border-brand-orange bg-brand-orange/10 text-brand-orange" : "border-clay text-stone hover:border-[#DDA15E]"}`}>
                  <span className={`flex items-center justify-center w-4 h-4 rounded border ${on ? "bg-brand-orange border-brand-orange" : "border-[#C9BCA8]"}`}>
                    {on && <Check size={11} className="text-white" />}
                  </span>
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>

        <button onClick={runCompare} disabled={loading} data-testid="compare-run"
          className="mt-8 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-ink text-white font-semibold hover:bg-[#413b36] transition-colors duration-300 disabled:opacity-60">
          {loading ? "Comparing…" : "Compare decisions"} <ArrowUpRight size={16} />
        </button>

        {result && (
          <div className="mt-12" data-testid="compare-results">
            {result.best && (
              <div className="rounded-3xl border border-moss/40 bg-moss/5 p-6 flex items-center gap-4 mb-6">
                <Trophy size={22} className="text-moss shrink-0" />
                <div>
                  <span className="label-tiny">Best fit for you right now</span>
                  <p className="font-display font-bold text-xl mt-1 text-ink">
                    {result.best.decision_label}{" "}
                    <span className="font-mono" style={{ color: bandColor(result.best.fit_band) }}>{result.best.fit}</span>{" "}
                    <span className="text-sm text-stone">· {result.best.probability}</span>
                  </p>
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {result.results.map((r, i) => {
                const c = bandColor(r.fit_band);
                return (
                  <div key={r.decision} data-testid={`compare-card-${r.decision}`} className="card-soft p-6 relative" style={i === 0 ? { borderColor: "rgba(92,107,78,0.4)" } : {}}>
                    <div className="flex items-center justify-between">
                      <span className="label-tiny">#{i + 1}</span>
                      <span className="label-tiny" style={{ color: c }}>{r.fit_band}</span>
                    </div>
                    <h3 className="font-display font-bold mt-3 text-ink">{r.decision_label}</h3>
                    <div className="font-mono text-4xl font-extrabold mt-3" style={{ color: c }}>{r.fit}</div>
                    <p className="text-sm text-stone mt-1">Success probability: {r.probability}</p>
                    <div className="h-2 bg-clay rounded-full overflow-hidden mt-4">
                      <div className="h-full rounded-full" style={{ background: c, width: `${r.fit}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
